import React, { useCallback, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useLocation, useParams } from 'react-router';
import { usePrefixedNavigate as useNavigate, getRoutePath } from '@edusense/navigation-utils';
import { MarkdownRenderer } from '@edusense/shared-ui-content';
import { BookOpen, FileText, LayoutTemplate, Lightbulb, ListChecks } from 'lucide-react';
import { InteractiveQuiz, splitMarkdownWithQuizBlocks, type QuizBlockSegment } from '@edusense/features-course';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #111827;
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #4b5563;
`;

const NavigationBar = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const NavButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 9999px;
  border: 1px solid ${({ $active }) => ($active ? '#2563eb' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#eff6ff' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#1d4ed8' : '#1f2937')};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2563eb;
    color: #1d4ed8;
  }
`;

const Section = styled.section`
  scroll-margin-top: 120px;
  padding: 24px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08);
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const PreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
  padding: 18px 20px;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
`;

const PreviewTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.8rem;
`;

const PreviewBody = styled.div`
  border-radius: 12px;
  border: 1px solid #eef2f7;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 50%);
  padding: 16px 18px;
  color: #0f172a;
  font-size: 0.92rem;
  line-height: 1.65;

  pre {
    background: #0f172a;
    color: #e2e8f0;
    padding: 12px;
    border-radius: 10px;
    overflow-x: auto;
  }

  code {
    background: rgba(15, 23, 42, 0.08);
    padding: 2px 5px;
    border-radius: 6px;
    font-size: 0.85rem;
  }
`;

const QuizPreviewNotice = styled.p`
  margin-top: 12px;
  font-size: 0.78rem;
  color: #64748b;
`;

const QuizFallback = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.85rem;
  color: #1f2937;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 14px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: linear-gradient(180deg, #f9fafb 0%, #ffffff 100%);
`;

const CardTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 600;
  color: #1f2937;
`;

const CardList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 20px;
  color: #4b5563;
  line-height: 1.5;
`;

const TipBox = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e3a8a;
`;

const TipIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #dbeafe;
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px dashed #e5e7eb;
  margin: 12px 0 24px 0;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #ffffff;
`;

const TableHeaderCell = styled.th`
  background: #f3f4f6;
  color: #111827;
  font-weight: 600;
  padding: 14px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
`;

const TableCell = styled.td`
  padding: 14px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  line-height: 1.5;
`;

const sections = [
  {
    id: 'markdown',
    icon: <FileText size={24} strokeWidth={1.8} />,
    title: '마크다운 에디터 사용법',
    summary: '긴 텍스트 편집, 문단 기반 콘텐츠 작성이 필요한 경우 빠르게 콘텐츠를 만들 수 있는 기본 에디터 모드입니다.'
  },
  {
    id: 'block',
    icon: <LayoutTemplate size={24} strokeWidth={1.8} />,
    title: '블록 에디터 사용법',
    summary: '퀴즈, 터미널 등 인터랙티브 블록을 조합해 강의를 구성할 때 사용하는 시각적 편집 도구입니다.'
  }
] as const;

const EditorUsageGuidePage: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const markdownSample = useMemo(() => `# 기능 소개\n\n> 마크다운 에디터에서는 **GitHub Flavored Markdown** 전체를 지원합니다.\n\n## 코드 블록\n\n\`\`\`bash\n# React 프로젝트 생성\npnpm create vite my-app\n\`\`\`\n\n## 체크리스트 \n\n- [x] 강의 목차 작성\n- [ ] 실습 코드 업로드\n- [ ] 퀴즈 검토\n\n:::quiz\ntype: multiple-choice\nquestion: 마크다운 에디터에서 지원하지 않는 기능은?\noptions:\n  - 드래그 앤 드롭 업로드\n  - 터미널 명령어 하이라이트\n  - 스토리북 배포 자동화\nanswerIndex: 2\nexplanation: 스토리북 배포는 별도의 파이프라인에서 처리해야 합니다.\n:::\n`, []);

  const blockExamples = useMemo(() => ([
    {
      id: 'markdown-block',
      label: '마크다운 블록',
      description: '텍스트와 이미지, 표 등 모든 기본 마크업을 작성할 수 있는 범용 블록입니다.',
      content: `### 개념 정리\n\n프론트엔드 상태 관리를 선택할 때는 **범용성**, **서드파티 생태계**, **러닝커브**를 비교하세요.\n\n| 옵션 | 장점 | 사용 시점 |\n| --- | --- | --- |\n| React Query | 서버 상태 관리 특화 | API 의존도가 높을 때 |\n| Zustand | 매우 가벼운 전역 상태 | 위젯 단위 상태 공유 |\n| Context | 의존성 없는 전달 | 테마, 로케일, 인증 정보 |`
    },
    {
      id: 'quiz-block',
      label: '퀴즈 블록',
      description: '학습자 이해도를 즉시 확인할 수 있도록 다지선다와 주관식을 지원합니다.',
      content: `:::quiz\ntype: short-answer\nquestion: HTTP 2.0에서 요청을 병렬 처리하기 위해 사용되는 핵심 기술은 무엇인가요?\nplaceholder: 예) ____\nanswer: Multiplexing\nexplanation: HTTP/2는 Multiplexing으로 단일 커넥션에서 다중 요청을 처리합니다.\n:::`
    },
    {
      id: 'terminal-block',
      label: '터미널 블록',
      description: '실습 환경에 필요한 명령어를 하이라이트된 형태로 전달할 수 있습니다.',
      content: `\`\`\`bash\n# CI 환경에서 의존성 설치\npnpm install --frozen-lockfile\n\n# 테스트 실행\npnpm test -- --runInBand\n\`\`\``
    },
  ]), []);

  const activeSection = useMemo(() => {
    if (section && sections.some(item => item.id === section)) {
      return section;
    }
    const hash = location.hash?.replace('#', '') ?? '';
    if (hash && sections.some(item => item.id === hash)) {
      return hash;
    }
    return sections[0].id;
  }, [section, location.hash]);

  useEffect(() => {
    if (!activeSection) return;
    const element = document.getElementById(activeSection);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeSection]);

  const handleNavigate = (targetId: typeof sections[number]['id']) => {
    const resolved = getRoutePath(`/learn/editor-guides/${targetId}`);
    const normalized = resolved.startsWith('/') ? resolved : `/${resolved}`;
    navigate(normalized);
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderPreviewSegments = useCallback((source: string, keyPrefix: string) => {
    const trimmed = source?.trim();
    if (!trimmed) {
      return <p key={`${keyPrefix}-empty`} style={{ color: '#94a3b8', fontSize: '0.85rem' }}>미리볼 콘텐츠가 없습니다.</p>;
    }

    const segments: QuizBlockSegment[] = splitMarkdownWithQuizBlocks(trimmed);
    if (!segments.length) {
      return <MarkdownRenderer key={`${keyPrefix}-markdown`} markdown={trimmed} />;
    }

    return segments.map((segment, index) => {
      if (segment.type === 'markdown') {
        return (
          <MarkdownRenderer
            key={`${keyPrefix}-markdown-${index}`}
            markdown={segment.content}
          />
        );
      }

      if (!segment.questions.length) {
        return (
          <QuizFallback key={`${keyPrefix}-quiz-fallback-${index}`}>
            {`퀴즈 블록 구문을 파싱할 수 없습니다. YAML 형식을 확인해주세요.\n\n예시:\n:::quiz\nid: quiz-1\ntype: multiple-choice\nquestion: 예시 문항\noptions:\n  - 선택지 1\n  - 선택지 2\nanswerIndex: 0\n:::`}
          </QuizFallback>
        );
      }

      return (
        <div key={`${keyPrefix}-quiz-${index}`} style={{ marginBottom: '18px' }}>
          <InteractiveQuiz quizData={segment.questions} onSubmit={() => undefined} />
          <QuizPreviewNotice>미리보기에서 즉시 실행되는 퀴즈 예시입니다. 저장 후 학습자 화면도 동일하게 동작합니다.</QuizPreviewNotice>
        </div>
      );
    });
  }, []);

  return (
    <Container>
        <Header>
          <Title>강의 에디터 가이드</Title>
          <Description>
            강의 편집 화면에서 제공하는 두 가지 에디터 모드를 빠르게 이해할 수 있도록 핵심 기능, 추천 사용 흐름, 자주 묻는 질문을 정리했습니다.
            강의 유형에 따라 적절한 에디터를 선택하고 생산성을 높여보세요.
          </Description>
          <NavigationBar>
            {sections.map(({ id, icon, title }) => (
              <NavButton
                key={id}
                type="button"
                $active={activeSection === id}
                onClick={() => handleNavigate(id)}
              >
                {icon}
                <span>{title}</span>
              </NavButton>
            ))}
          </NavigationBar>
        </Header>

        <Section id="markdown">
          <SectionTitle>
            <FileText size={24} strokeWidth={1.8} />
            마크다운 에디터 사용법
          </SectionTitle>
          <Description>
            텍스트 위주의 강의를 빠르게 작성할 수 있는 표준 코드 기반 편집기입니다. 라이브 미리보기, 드래그 앤 드롭 업로드, 커스텀 블록 문법을 모두 지원합니다.
          </Description>
          <SectionDivider />
          <PreviewGrid>
            <PreviewCard>
              <PreviewHeader>
                <span>라이브 렌더링 예시</span>
                <PreviewTag>
                  <FileText size={14} /> MarkdownRenderer
                </PreviewTag>
              </PreviewHeader>
              <PreviewBody>
                {renderPreviewSegments(markdownSample, 'markdown-sample')}
              </PreviewBody>
            </PreviewCard>
            <PreviewCard>
              <PreviewHeader>
                <span>작성 팁</span>
                <PreviewTag>Best Practice</PreviewTag>
              </PreviewHeader>
              <PreviewBody>
                <ul>
                  <li>구분선 <code>---</code> 을 활용하면 블록 모드 전환 시 자동으로 구획이 나뉩니다.</li>
                  <li>커스텀 블록 문법(`:::quiz`, `:::video`)도 동일한 렌더러로 즉시 검증 가능합니다.</li>
                  <li>이미지, 비디오를 드래그하여 업로드하면 경로가 자동 삽입됩니다.</li>
                </ul>
              </PreviewBody>
            </PreviewCard>
          </PreviewGrid>
          <SectionGrid>
            <Card>
              <CardTitle>핵심 기능</CardTitle>
              <CardList>
                <li>좌측 입력, 우측 미리보기 없이 단일 에디터에서 즉시 렌더링 상태 확인</li>
                <li>이미지·비디오 파일 드롭 시 자동 업로드 및 마크다운 링크 삽입</li>
                <li>코드 블록, 체크리스트 등 GitHub Flavored Markdown 전반 지원</li>
                <li>커스텀 블록 (`:::quiz`, `:::video`) 입력 시 실시간 구문 강조</li>
              </CardList>
            </Card>
            <Card>
              <CardTitle>추천 작성 흐름</CardTitle>
              <CardList>
                <li>1) 강의 개요 작성 → 2) 섹션별 세부 내용 정리 → 3) 멀티미디어 링크 삽입</li>
                <li>단축키 <code>Ctrl/Cmd + S</code> 로 저장, <code>Ctrl/Cmd + /</code> 로 주석 토글 활용</li>
                <li>구획 구분은 `---` 구분선을 사용하면 블록 에디터 전환 시에도 정확히 분리</li>
                <li>작성 중 자주 쓰는 스니펫은 VS Code 마크다운 확장과 동일한 문법을 활용</li>
              </CardList>
            </Card>
            <Card>
              <CardTitle>검수 체크리스트</CardTitle>
              <CardList>
                <li>헤딩 레벨이 순차적으로 내려가는지 (H2 → H3 → H4)</li>
                <li>이미지 ALT 텍스트, 비디오 제목이 모두 채워졌는지</li>
                <li>표와 코드 블록이 모바일에서도 잘리는지 미리보기 확인</li>
                <li>커스텀 블록 문법에 오탈자가 없는지 (`type`, `answerIndex` 등)</li>
              </CardList>
            </Card>
          </SectionGrid>
          <TipBox>
            <TipIcon>
              <Lightbulb size={18} strokeWidth={2} />
            </TipIcon>
            <div>
              <strong>Tip.</strong> 기존 마크다운 파일을 붙여넣을 때는 `마크다운 임포트` 기능을 사용하면 자동으로 블록이 분리되어 블록 에디터 전환 시에도 구조가 유지됩니다.
            </div>
          </TipBox>
        </Section>

        <Section id="block">
          <SectionTitle>
            <LayoutTemplate size={24} strokeWidth={1.8} />
            블록 에디터 사용법
          </SectionTitle>
          <Description>
            드래그 앤 드롭으로 콘텐츠 블록을 재배치하고, 각 블록별로 고유한 입력 폼을 제공해 복합 강의를 만들 때 유용합니다.
          </Description>
          <SectionDivider />
          <PreviewGrid>
            {blockExamples.map(block => (
              <PreviewCard key={block.id}>
                <PreviewHeader>
                  <span>{block.label}</span>
                  <PreviewTag>{block.id.replace('-block', '').toUpperCase()}</PreviewTag>
                </PreviewHeader>
                <Description>{block.description}</Description>
                <PreviewBody>
                  {renderPreviewSegments(block.content, `block-${block.id}`)}
                </PreviewBody>
              </PreviewCard>
            ))}
          </PreviewGrid>
          <SectionGrid>
            <Card>
              <CardTitle>편집 패턴</CardTitle>
              <CardList>
                <li>`+ 블록 추가` 버튼이나 드롭존을 사용해 빈 공간에 블록 삽입</li>
                <li>블록 헤더에서 접기/펼치기, 복제, 삭제를 바로 실행</li>
                <li>각 블록의 미리보기 패널을 통해 학습자 시각의 결과물을 즉시 확인</li>
                <li>마크다운 전환 시 블록 순서와 내용이 그대로 유지되도록 구조화 저장</li>
              </CardList>
            </Card>
            <Card>
              <CardTitle>주요 블록 종류</CardTitle>
              <CardList>
                <li><strong>마크다운</strong>: 일반 텍스트와 이미지, 커스텀 블록 포함 가능</li>
                <li><strong>표</strong>: 커리큘럼 요약, 비교표 작성 시 사용</li>
                <li><strong>터미널</strong>: CLI 데모, 코드 출력 설명에 사용</li>
                <li><strong>퀴즈</strong>: 다지선다/주관식 등 학습 확인용 인터랙티브 요소</li>
                <li><strong>이미지</strong>: 캡션 포함 이미지 전용 블록</li>
              </CardList>
            </Card>
            <Card>
              <CardTitle>팀 협업 체크</CardTitle>
              <CardList>
                <li>여러 명이 동시에 편집할 때는 저장 버튼으로 명시적인 버전 고정</li>
                <li>섹션 이동 후에는 `변경사항 저장` 버튼이 활성화 되었는지 확인</li>
                <li>블록 삭제는 되돌릴 수 없으므로, 삭제 전 복제 후 주석 처리 권장</li>
                <li>외부 링크/이미지 URL은 `리소스` 탭에서 공용 자산으로 관리</li>
              </CardList>
            </Card>
          </SectionGrid>
          <TipBox>
            <TipIcon>
              <ListChecks size={18} strokeWidth={2} />
            </TipIcon>
            <div>
              <strong>Tip.</strong> 커스텀 블록 문법이 익숙하지 않은 경우 <strong>커스텀 블록 가이드</strong> 페이지를 참고하면 블록 에디터에서 바로 복사해 사용할 수 있는 예제를 확인할 수 있습니다.
            </div>
          </TipBox>
        </Section>

        <Section>
          <SectionTitle>
            <BookOpen size={24} strokeWidth={1.8} />
            에디터 선택 가이드라인
          </SectionTitle>
          <ComparisonTable>
            <thead>
              <tr>
                <TableHeaderCell>구분</TableHeaderCell>
                <TableHeaderCell>마크다운 에디터</TableHeaderCell>
                <TableHeaderCell>블록 에디터</TableHeaderCell>
              </tr>
            </thead>
            <tbody>
              <tr>
                <TableCell>추천 용도</TableCell>
                <TableCell>텍스트 중심 설명형 강의, 빠른 초안 작성</TableCell>
                <TableCell>인터랙티브 요소가 많은 실습형 강의, 시각적 구성</TableCell>
              </tr>
              <tr>
                <TableCell>콘텐츠 구조</TableCell>
                <TableCell>마크다운 문법 기반 단일 문서</TableCell>
                <TableCell>블록 단위로 모듈화된 구조</TableCell>
              </tr>
              <tr>
                <TableCell>강점</TableCell>
                <TableCell>빠른 입력, 단축키 친화적, 버전 비교 용이</TableCell>
                <TableCell>시각적 피드백, 블록별 권한 제어 준비</TableCell>
              </tr>
              <tr>
                <TableCell>주의사항</TableCell>
                <TableCell>복잡한 레이아웃은 구현이 어려움</TableCell>
                <TableCell>대량 텍스트 입력 시 속도가 느릴 수 있음</TableCell>
              </tr>
            </tbody>
          </ComparisonTable>
        </Section>
      </Container>
  );
};

export default EditorUsageGuidePage;
